<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';
  import { Input, Tooltip } from '@sveltestrap/sveltestrap';
  import { Controls, DefaultUi, Player, Video, Vimeo, Youtube } from '@vime/svelte';

  import { Icon } from '@client/components/icon';
  import { RuleBlock } from '@client/components/rule-block';

  import { AssetsService } from '@client/services/assets';

  import { sanitizeRulenameOnKeyUp } from '@client/utils/sanitize';

  import { secondsToHms } from '@common/utils/numbers';

  export let data = {};
  export let invalidRulename = false;

  let currentRulenames = [];

  onMount(async () => {
    const response = await AssetsService.getAllVideos();

    currentRulenames = response.map(({ rulename }) => rulename).filter((rulename) => rulename !== data.rulename);
  });

  $: invalidRulename = currentRulenames.includes(data.rulename.toLowerCase());
</script>

<div class="video-edit-form">
  <div class="section">
    <div class="heading">
      {$format('label.NAME')} <Icon name="help-circle" id="rulename-info" class="cursor-help h5" />
    </div>
    <Tooltip target="rulename-info" placement="top">{$format('label.TOOLTIP.ASSET_VIDEO_RULENAME')}</Tooltip>
    <div class="copy-field" class:invalid={!data?.rulename || invalidRulename}>
      <RuleBlock text="[[vid:{data.rulename}]]" />
      <Input
        type="text"
        class="rulename"
        placeholder={$format('label.PLACEHOLDER.INPUT', {
          values: { param: $format('label.NAME').toLowerCase() }
        })}
        invalid={!data.rulename || invalidRulename}
        feedback={!data.rulename
          ? $format('label.VALIDATION_TOO_SHORT', { values: { param: $format('label.NAME'), length: 1 } })
          : $format('label.VALIDATION_NAME_EXISTS', {
              values: { type: $format('label.VIDEO'), param: $format('label.NAME').toLowerCase() }
            })}
        on:keyup={() => (data.rulename = sanitizeRulenameOnKeyUp(data.rulename))}
        bind:value={data.rulename}
      />
    </div>
  </div>
  <div class="section">
    <div class="heading">
      {$format('label.VIDEO_DETAILS')}
    </div>
    <div class="asset-details">
      <div class="asset-item">
        <div class="video">
          <div class="asset-info">
            <b>{$format('label.PREVIEW')}</b>
            <small>{$format('label.RESOLUTION')} {data.width}x{data.height}</small>
            <small>{$format('label.LENGTH')} {secondsToHms(data.duration)}</small>
          </div>
          <div class="asset-actions">
            <div class="video-preview">
              <Player>
                {#if data.videotype === 'youtube'}
                  <Youtube videoId={data.vidid} />
                {:else if data.videotype === 'vimeo'}
                  <Vimeo videoId={data.vidid} />
                {:else}
                  <Video crossOrigin="" poster={data.thumbnail}>
                    <source data-src={data.url} type="video/mp4" />
                  </Video>
                {/if}
                <DefaultUi>
                  <Controls hideWhenPaused />
                </DefaultUi>
              </Player>
            </div>
          </div>
        </div>
      </div>
      <div class="asset-item">
        <div class="asset-info">
          <b>{$format('label.URL')}</b>
          <p>{$format('label.DESCRIPTION.ASSET_VIDEO_URL')}</p>
        </div>
        <div class="asset-actions">
          <div class="url">
            <Input type="text" bind:value={data.url} placeholder={$format('label.PLACEHOLDER.ASSET_VIDEO_URL')} />
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<style lang="scss">
  .video-edit-form {
    .section {
      &:first-child {
        margin-top: var(--margin-base);
      }

      + .section {
        margin-top: var(--margin-xlarge);
      }

      .heading {
        font-size: var(--font-size-large);
        font-weight: var(--font-weight-medium);
        margin-bottom: var(--margin-base);
      }
    }


    .copy-field {
      position: relative;

      :global(.rule) {
        position: absolute;
        right: 5px;
        top: 5px;
      }

      &.invalid {
        :global(.rule) {
          display: none;
        }
      }
    }

    :global(.invalid-feedback) {
      color: var(--color-red-500);
      font-size: var(--font-size-medium);
      margin-top: var(--margin-base);
    }

    .asset-details {
      border: 1px solid var(--border-color);
      border-radius: var(--border-radius-base);
      padding: var(--padding-small) var(--padding-base);

      .asset-item {
        display: flex;
        padding: var(--padding-large);

        + .asset-item {
          border-top: 1px solid var(--border-color);
        }

        .video {
          display: flex;
          flex: 1;
        }

        .asset-info {
          align-items: center;
          flex: 1;

          b {
            color: var(--color-gray-900);
          }

          small {
            color: var(--color-gray-600);
            display: block;
            margin-top: var(--margin-xsmall);
          }
        }

        .asset-actions {
          align-items: center;
          display: flex;
          justify-content: flex-end;
          gap: 10px;

          .video-preview {
            min-width: 300px;

            &:hover {
              opacity: 0.8;
            }

            img {
              background-size: contain !important;
              border: 1px solid var(--border-color);
              border-radius: var(--border-radius-base);
              display: block;
              height: 120px;
              width: auto;
            }
          }

          .url {
            width: 300px;
          }
        }
      }
    }
  }

  @media only screen and (max-width: 760px) {
    .video-edit-form {
      .asset-details {
        .asset-item {
          flex-direction: column;

          .asset-actions {
            justify-content: flex-start;
            margin-top: var(--margin-large);

            .url {
              width: 100%;
            }
          }
        }
      }
    }
  }

  @media only screen and (max-width: 420px) {
    .video-edit-form {
      .asset-details {
        .asset-item {
          .video {
            display: block;
          }

          .asset-actions {
            display: block;
            margin-top: var(--margin-large);
          }
        }
      }
    }
  }
</style>
